import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  fetchUsers,
  fetchParentShare,
  clearParentShare,
  resetPassword,
  toggleMatkaLock,
  USER_TYPES,
  USER_TYPE_NAMES,
} from "../store/actions/userManagement";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from "../config/baseUrl";
import {
  canNavigateDown,
  getNextLevelUserType,
} from "../utils/downlineHelpers";
import UserExposureModal from "../components/UserExposureModal";
import ClientExposureModal from "../components/ClientExposureModal";
import { toast } from "react-toastify";
import { formatDisplayUserId } from "../utils/userIdFormat";
import UserActionsDropdown from "../components/UserActionsDropdown";
import { useGetUsersByTypeQuery, useToggleUserStatusMutation } from "../services/auth/authApi";
import SearchDropdown, { SearchIcon } from "../components/SearchDropdown";
import PartnershipModal from "../components/PartnershipModal";
import TransactionModal from "../components/TransactionModal";
import PasswordModal from "../components/PasswordModal";
import BlockActionModal from "../components/BlockActionModal";
import { getDomainAppUrl, getSiteName } from "../utils/domainConfig";
import { getCurrentUserLevel, USER_LEVELS } from "../config/menuItems";
import {
  activateDeactivateUser,
  toggleBetLockApi,
  toggleCasinoLockApi,
  searchUsersApi,
  updateBettingStatusApi,
} from "../services/userListApi";

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

const UpIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3A8 8 0 00140 768h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path>
  </svg>
);

const DownIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor" aria-hidden="true">
    <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 0 0 0 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path>
  </svg>
);

// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const UserListPage = ({ userType, title, customData, isDeadClient = false }) => {
  const navigate = useNavigate();
  // Passwords are only ever shown in plaintext to the top-level Admin —
  // every other level sees a masked value and loses the WhatsApp-share button.
  const isAdminViewer = getCurrentUserLevel() === USER_LEVELS.ADMIN;
  const { users, totalPages, currentPage, parentShare, loading, errors } =
    useSelector(function (state) {
      return state.userManagement;
    });

    const accessToken = useSelector(function (state) {
  return state.auth.accessToken;
});

  // Search query state - must be declared before RTK Query hook
  const [searchQuery, setSearchQuery] = useState('');
  const [currentRTKPage, setCurrentRTKPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // RTK Query hook to fetch users by type (skip if customData is provided or isDeadClient)
  const userTypeNameForQuery = USER_TYPE_NAMES[userType];
  const { data: usersByType, isLoading: usersByTypeLoading, isFetching: usersByTypeFetching, error: usersByTypeError, refetch: refetchUsers } = useGetUsersByTypeQuery(
    { userType: userTypeNameForQuery, search: searchQuery, page: currentRTKPage, limit: itemsPerPage },
    {
      skip: !!customData || isDeadClient // Skip API call if customData is provided or if fetching dead clients
    }
  );
  
  // RTK Mutation for toggle user status
  const [toggleUserStatus, { isLoading: isTogglingStatus }] = useToggleUserStatusMutation();
  
  // Transform RTK Query data to match existing table structure
  const transformedRTKUsers = (customData || usersByType?.data)?.map(user => ({
    id: user.id || user._id,
    userId: user.userId,
    userName: user.username,
    userType: user.userType,
    parentId: user.parentId ? {
      _id: user.parentId._id,
      userId: user.parentId.userId,
      username: user.parentId.username,
      userType: user.parentId.userType
    } : null,
    fullName: user.fullName,
    reference: user.reference,
    contact: user.contactNo,
    isActive: user.isActive,
    parentName: user.parentId?.fullName,
    createdOn: user.createdAt,
    partnerShip: user.shares?.myShare || 0,
    matchCommission: user.shares?.matchCommission || 0,
    sessionCommission: user.shares?.sessionCommission || 0,
    casinoShare: user.shares?.casinoShare || 0,
    casinoCommission: user.shares?.casinoCommission || 0,
    matkaCommission: user.shares?.matkaCommission || 0,
    balance: user.balance || 0,
    balanceWithPnl: 0,
    liability: user.exposure || 0,
    betLock: false,
    casinoLock: false,
    matkaBetLock: false,
    commisstionType: 1, // Default NOC
    exposureLimit: user.exposureLimit || 0,
    plainPassword: user.plainPassword || null
  })) || [];
  

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [partnershipModal, setPartnershipModal] = useState({
    isOpen: false,
    userId: null,
  });
  const [transactionModal, setTransactionModal] = useState({
    isOpen: false,
    type: null,
    user: null,
  });
  const [passwordModal, setPasswordModal] = useState({
    isOpen: false,
    user: null,
  });
  const [exposureModal, setExposureModal] = useState({
    isOpen: false,
    user: null,
  });
  const [clientExposureModal, setClientExposureModal] = useState({
    isOpen: false,
    user: null,
  });
  const [blockActionModal, setBlockActionModal] = useState({
    isOpen: false,
    user: null,
  });

  // Search states
  const [searchDropdown, setSearchDropdown] = useState({
    isOpen: false,
    field: null,
    position: { top: 0, left: 0 },
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [deadClientData, setDeadClientData] = useState([]);
  const [deadClientLoading, setDeadClientLoading] = useState(false);

  // Fetch dead clients if isDeadClient is true
  useEffect(
    function () {
      if (isDeadClient) {
        fetchDeadClients();
      } else {
        fetchUsers(userType, "", 0, 250);
      }
    },
    [userType, isDeadClient],
  );

  const fetchDeadClients = async function() {
    try {
      setDeadClientLoading(true);
      const response = await axios.post(
        API_BASE_URL + "/user/list-user-v2",
        {
          userType: userType.toString(),
          parentId: "",
          noOfRecords: 250,
          index: 0,
          forDeadClient: true
        },
        { 
          headers: { 
            Authorization: "Bearer " + accessToken 
          } 
        }
      );

      if (response.data?.status !== false && response.data?.data?.userList) {
        const transformedData = response.data.data.userList.map(user => ({
          id: user._id,
          userId: user.userId,
          userName: user.userName,
          userType: user.userType,
          parentId: user.parentId,
          fullName: user.fullName,
          contact: user.contactNo,
          isActive: user.isActive,
          parentName: user.parentName,
          createdOn: user.createdOn,
          partnerShip: user.partnerShip || 0,
          matchCommission: user.matchCommission || 0,
          sessionCommission: user.sessionCommission || 0,
          casinoShare: user.casinoShare || 0,
          casinoCommission: user.casinoCommission || 0,
          matkaCommission: user.matkaCommission || 0,
          balance: user.balance || 0,
          balanceWithPnl: user.balanceWithPnl || 0,
          liability: user.liability || 0,
          betLock: user.betLock || false,
          casinoLock: user.casinoLock || false,
          matkaBetLock: user.matkaBetLock || false,
          commisstionType: user.commisstionType || 1,
          exposureLimit: user.exposureLimit || 0
        }));
        setDeadClientData(transformedData);
      } else {
        toast.error(response.data?.message || "Failed to fetch dead clients");
      }
    } catch (error) {
      console.error("Error fetching dead clients:", error);
      toast.error("Failed to fetch dead clients");
    } finally {
      setDeadClientLoading(false);
    }
  };

  useEffect(
    function () {
      if (!isDeadClient) {
        fetchUsers(userType, "", 0, 250);
      }
    },
    [userType],
  );



  // Get route path for user type
  const getRouteForUserType = function (type) {
    const routes = {
      [USER_TYPES.SUPERADMIN]: "/admin-details/super-admin",
      [USER_TYPES.ADMIN]: "/admin-details/admin",
      [USER_TYPES.MINIADMIN]: "/admin-details/mini-admin",
      [USER_TYPES.MASTER]: "/admin-details/master",
      [USER_TYPES.SUPERMASTER]: "/admin-details/super-master",
      [USER_TYPES.AGENT]: "/admin-details/agent",
      [USER_TYPES.CLIENT]: "/admin-details/client",
    };
    return routes[type];
  };

  const handleUserClick = function (user) {
    // If this is CLIENT level, show client exposure modal
    if (userType === USER_TYPES.CLIENT) {
      setClientExposureModal({ isOpen: true, user: user });
      return;
    }
    const userTypeObj = {
      "admin":"super admin",
      "SuperAdmin":"mini admin",
      "MiniAdmin":"master",
      "Master":"super master",
      "Super":"agent",
      "Agent":"client"
    }
    // For all other levels, navigate to their downline
    // SuperAdmin (7) → MiniAdmin, MiniAdmin (5) → Master, etc.
    const adminId = user.id || user._id || user.userId;
    if (adminId) {
      const route = `/admin-details/downline-by-id/${adminId}/${userType}/${encodeURIComponent(userTypeObj[user.userType])}`;
      navigate(route);
    } else {
      // Fallback to old navigation if no adminId
      navigate(
        `/admin-details/downline/${user.userId}/${userType}/${encodeURIComponent(user.userName)}`,
      );
    }
  };

  const formatDate = function (dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return day + "-" + month + "-" + year;
  };

  const getCommissionType = function (type) {
    const types = {
      1: "NOC", // No Commission
      2: "BBB", // BetByBet
      3: "BBB",
    };
    return types[type] || "-";
  };

  const handlePartnershipClick = function (user) {
    setPartnershipModal({ isOpen: true, userId: user.userId, mongoId: user.id });
  };

  const handleClosePartnership = function () {
    setPartnershipModal({ isOpen: false, userId: null, mongoId: null });
  };

  const handleDropdownClick = function (e, userId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  const handleCloseDropdown = function () {
    setActiveDropdown(null);
  };

  // Handle search icon click
  const handleSearchIconClick = function (e, field) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setSearchDropdown({
      isOpen: true,
      field: field,
      position: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 100, // Adjust to align better
      },
    });
  };

  // Handle search submit
  const handleSearch = async function () {
    if (!searchTerm.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setSearchLoading(true);
    const result = await searchUsersApi(userType, searchTerm);
    setSearchLoading(false);

    if (result.success) {
      setSearchedUsers(result.data.userListV2 || []);
      setIsSearchActive(true);
      setSearchDropdown({
        isOpen: false,
        field: null,
        position: { top: 0, left: 0 },
      });

      if (!result.data.userListV2 || result.data.userListV2.length === 0) {
        toast.info("No users found matching your search");
      }
    } else {
      toast.error(result.error || "Search failed");
    }
  };

  // Handle search reset
  const handleSearchReset = function () {
    setSearchTerm("");
    setSearchedUsers([]);
    setIsSearchActive(false);
    setSearchDropdown({
      isOpen: false,
      field: null,
      position: { top: 0, left: 0 },
    });
  };

  const handleAction = async function (action, user) {
    setActiveDropdown(null);

    switch (action) {
      case "deposit":
        setTransactionModal({ isOpen: true, type: "deposit", user: user });
        break;
      case "withdraw":
        setTransactionModal({ isOpen: true, type: "withdraw", user: user });
        break;
      case "toggleStatus":
        // Use RTK mutation to toggle user status
        try {
          const userId = user.id || user._id;
          await toggleUserStatus(userId).unwrap();
          // toast.success("User status updated successfully!");
          
          // Refresh the data
          if (isSearchActive) {
            handleSearch(); // Re-run search to refresh
          } else if (usersByType) {
            refetchUsers(); // Refetch RTK query data
          } else {
            fetchUsers(userType, "", currentPage, 250);
          }
        } catch (err) {
          // toast.error(err?.data?.message || "Failed to update user status");
        }
        break;
      case "blockMarket":
        setBlockActionModal({ isOpen: true, user: user });
        break;
      case "toggleBetLock":
        // isLock: true to lock, isLock: false to unlock
        const betLockResult = await toggleBetLockApi(
          user.userId,
          !user.betLock,
        );
        if (betLockResult.success) {
          toast.success(
            betLockResult.message || "Bet lock status updated successfully!",
          );
          if (isSearchActive) {
            handleSearch(); // Re-run search to refresh
          } else {
            fetchUsers(userType, "", currentPage, 250);
          }
        } else {
          toast.error(
            betLockResult.error || "Failed to update bet lock status",
          );
        }
        break;
      case "toggleCasinoLock":
        // isLock: true to lock, isLock: false to unlock
        const casinoLockResult = await toggleCasinoLockApi(
          user.userId,
          !user.casinoLock,
        );
        if (casinoLockResult.success) {
          toast.success(
            casinoLockResult.message ||
              "Casino lock status updated successfully!",
          );
          if (isSearchActive) {
            handleSearch(); // Re-run search to refresh
          } else {
            fetchUsers(userType, "", currentPage, 250);
          }
        } else {
          toast.error(
            casinoLockResult.error || "Failed to update casino lock status",
          );
        }
        break;
      case "toggleMatkaLock":
        // isLock: true to lock, isLock: false to unlock
        const matkaLockResult = await toggleMatkaLock(
          user.userId,
          !user.matkaBetLock,
        );
        if (matkaLockResult.success) {
          toast.success(
            matkaLockResult.message ||
              "Matka lock status updated successfully!",
          );
          if (isSearchActive) {
            handleSearch(); // Re-run search to refresh
          } else {
            fetchUsers(userType, "", currentPage, 250);
          }
        } else {
          toast.error(
            matkaLockResult.error || "Failed to update matka lock status",
          );
        }
        break;
      case "resetPassword":
        setPasswordModal({ isOpen: true, user: user });
        break;
      case "edit":
        const editUserId = user.id || user._id || user.userId;
        navigate("/admin-details/edit/" + editUserId);
        break;
      case "statement":
        console.log('Navigating to statement with userId:', user.userId);
        console.log('Full user object:', user);
        const statementUserId = user.id || user._id || user.userId;
        navigate("/admin-details/statement/" + statementUserId);
        break;
      case "accountOps":
        const accountOpsUserId = user.id || user._id || user.userId;
        navigate("/admin-details/account-operations/" + accountOpsUserId);
        break;
      case "loginReport":
        const loginReportUserId = user.id || user._id || user.userId;
        navigate("/admin-details/login-report/" + loginReportUserId);
        break;
      case "commissionReport":
        { const commUserId = user.id || user._id;
        navigate("/ledger/comm-lenden?userId=" + commUserId);
        break; }
      case "downline":
        // Navigate to downline (lower level users)
        handleUserClick(user);
        break;
      default:
        break;
    }
  };

  const handleTransactionSuccess = function () {
    if (isSearchActive) {
      handleSearch(); // Re-run search to refresh
    } else {
      fetchUsers(userType, "", currentPage, 250);
    }
  };

  const handleBlockAction = async function (actionType, user) {
    const userId = user.id || user._id;
    let type = "all";
    let currentStatus = false;
    
    // Map action type to API type and get current status
    switch (actionType) {
      case "fancy":
        type = "fancy";
        currentStatus = user.fancyLock || false;
        break;
      case "betting":
        type = "bookmaker";
        currentStatus = user.betLock || false;
        break;
      case "casino":
        type = "casino";
        currentStatus = user.casinoLock || false;
        break;
      default:
        type = "all";
    }
    
    // Toggle the status
    const newStatus = !currentStatus;
    
    const result = await updateBettingStatusApi(userId, newStatus, type, true);
    
    if (result.success) {
      toast.success(
        result.message || `${type.charAt(0).toUpperCase() + type.slice(1)} lock status updated successfully!`,
      );
      if (isSearchActive) {
        handleSearch();
      } else if (usersByType) {
        refetchUsers();
      } else {
        fetchUsers(userType, "", currentPage, 250);
      }
    } else {
      toast.error(
        result.error || `Failed to update ${type} lock status`,
      );
    }
  };

  // Determine which users to display
  const displayUsers = isSearchActive 
    ? searchedUsers 
    : isDeadClient 
      ? deadClientData
      : usersByType?.data ? transformedRTKUsers : users;

  const filteredUsers = displayUsers.filter(function (user) {
    // For dead clients, show inactive users; otherwise show active users
    if (isDeadClient) {
      return !user.isActive; // Show only inactive users for dead clients
    } else {
      return user.isActive; // Show only active users for normal lists
    }
  });

  const userTypeName = USER_TYPE_NAMES[userType] || "User";
  const userTypeKey =
    Object.keys(USER_TYPES)
      .find(function (key) {
        return USER_TYPES[key] === userType;
      })
      ?.toLowerCase() || "user";
  const formatUserId = function (userId) {
    return formatDisplayUserId(userId);
  };

  // Check if there's a lower level to navigate to
  const hasLowerLevel = userType > USER_TYPES.CLIENT; // All levels above Client (1) can navigate down
  // Client level should be clickable to show exposure modal
  const isClientLevel = userType === USER_TYPES.CLIENT;
  const isClickable = hasLowerLevel || isClientLevel;
  

  return (
    <div className="w-full">
      <div className="bg-white shadow">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#1c1c22] to-[#26262c] border-b border-[#303039] px-3 sm:px-5 py-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-gradient-to-r after:from-transparent after:via-indigo-500 after:to-transparent">
          <span className="text-lg sm:text-xl text-white font-normal capitalize">
            {title || userTypeName + " Details"}
          </span>
          <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
            
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                style={{color:"white"}}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-3 py-1.5 rounded border border-gray-300 focus:outline-none focus:border-blue-500 w-60 sm:min-w-[200px]"
              />
       
            {isSearchActive && (
              <button
                onClick={handleSearchReset}
                className="bg-red-500 text-white px-3 sm:px-4 py-1 rounded hover:bg-red-600 text-sm whitespace-nowrap"
              >
                Clear Search
              </button>
            )}
            <button
              onClick={function () {
                navigate(-1);
              }}
              className="bg-blue-500 text-white px-3 sm:px-4 py-1 rounded hover:bg-blue-600 text-sm whitespace-nowrap"
            >
              Back
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-3 py-3 flex justify-around gap-4">
          <Link
            to={"/admin-details/create/" + userTypeKey}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            + Create
          </Link>
          <Link
            to={"/admin-details/update-limit/" + userTypeKey}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Update Limit
          </Link>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {(loading.list && !isSearchActive && !usersByType && !customData && !isDeadClient) || 
           (usersByTypeLoading && !customData && !isDeadClient) ||
           (usersByTypeFetching && !customData && !isDeadClient) ||
           (deadClientLoading && isDeadClient) ? (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          ) : ((errors.list && !isSearchActive && !usersByType && !customData && !isDeadClient) || 
                (usersByTypeError && !customData && !isDeadClient)) ? (
            <div className="p-4 text-red-500 text-center">
              {errors.list || usersByTypeError?.message || 'Failed to load users'}
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#26262c] text-slate-200 border-y">
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">#</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Action</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div className="flex items-center gap-1">
                        <span className="ant-table-column-title">User</span>
                        <button
                          onClick={function (e) {
                            handleSearchIconClick(e, "code");
                          }}
                          className="cursor-pointer hover:text-blue-300"
                          title="Filter menu"
                        >
                          <SearchIcon />
                        </button>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title capitalize">Name</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title capitalize">Reference</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title capitalize">Super Admin</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Password</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Share</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center" colSpan="4">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Mini Admin Comm %</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Coins</span>
                      </div>
                    </span>
                  </th>
                  {userType === USER_TYPES.CLIENT && (
                    <th className="p-2 border text-left" rowSpan="2">
                      <span className="ant-table-header-column">
                        <div>
                          <span className="ant-table-column-title">Exposure</span>
                        </div>
                      </span>
                    </th>
                  )}
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Status</span>
                      </div>
                    </span>
                  </th>
                </tr>
                <tr className="bg-[#26262c] text-slate-200 border-y">
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Match Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Sess Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Casino Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Matka Comm</span>
                      </div>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={userType === USER_TYPES.CLIENT ? "15" : "14"}
                      className="p-8 text-center text-gray-500"
                    >
                      {isSearchActive
                        ? "No users found matching your search"
                        : "No " + userTypeName.toLowerCase() + "s found"}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(function (user, index) {
                    const rowBgColor = index % 2 === 0 ? "var(--row-even)" : "var(--row-odd)";
                    return (
                      <tr
                        key={user.userId}
                        style={{ backgroundColor: rowBgColor }}
                      >
                        <td className="p-2 border">
                          <button
                            onClick={function () {
                              handlePartnershipClick(user);
                            }}
                            className="bg-blue-500 text-white p-1 rounded"
                            title="View Partnership"
                          >
                            <PlusIcon />
                          </button>
                        </td>
                        <td className="p-2 border">
                          <button
                            onClick={function (e) {
                              handleDropdownClick(e, user.userId);
                            }}
                            className="bg-blue-500 text-white p-1 rounded"
                          >
                            <MenuIcon />
                          </button>
                        </td>
                        <td className="p-2 border">
                          {isClickable ? (
                            <a
                              href="#"
                              className="text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center justify-start"
                              onClick={function (e) {
                                e.preventDefault();
                                handleUserClick(user);
                              }}
                            >
                              <EyeIcon />
                              <span style={{paddingLeft:"5px"}} className="whitespace-nowrap">
                                {user.userId} 
                              </span>
                            </a>
                          ) : (
                            <span className="text-xs text-gray-600 whitespace-nowrap">{user.userId} ({user.userName})</span>
                          )}
                        </td>
                        <td className="p-2 border">
                         {user.fullName}
                        </td>
                        <td className="p-2 border">
                          {user.reference || '-'}
                        </td>
                        <td className="p-2 border">
                          {user.parentName || user.parentId?.username || '-'}
                          {user.parentId?.userId && (
                            <span className="text-xs text-gray-500 ml-2">({user.parentId.userId})</span>
                          )}
                        </td>
                        <td className="p-2 border">
                          <div className="flex items-center justify-between gap-2">
                            <span>{isAdminViewer ? (user.plainPassword || '***') : '••••••••'}</span>
                            {isAdminViewer && (
                            <button
                              className="bg-green-500 text-white px-2 py-1 text-xs rounded hover:bg-green-600 flex items-center gap-1"
                              onClick={function() {
                                const rawUrl = user.appUrl?.url || null;
                                const isClient = (user.userType === 1 || String(user.userType).toLowerCase() === 'client');
                                const domain = rawUrl
                                  ? (rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`)
                                  : isClient
                                    ? `https://${getDomainAppUrl()}`
                                    : `https://admin.${getDomainAppUrl()}`;

                                const username = user.username || user.userName || user.userId || '';
                                const password = user.plainPassword || '***';

                                const message =
                                  `🏏 *Welcome to ${getSiteName()}!* 🏏\n` +
                                  `${'━'.repeat(28)}\n\n` +
                                  `🆔 *Username :* ${username}\n` +
                                  `🔑 *Password :* ${password}\n` +
                                  `🌐 *Website  :* ${domain}\n\n` +
                                  `${'━'.repeat(28)}\n` +
                                  `⚠️ _Keep your credentials safe._\n` +
                                  `_Do not share with anyone._`;

                                const encodedMessage = encodeURIComponent(message);
                                const phoneNumber = user.phone || user.phoneNumber || user.mobile || user.mobileNumber;

                                let whatsappUrl;
                                if (phoneNumber) {
                                  const cleanPhone = phoneNumber.toString().replace(/\D/g, '');
                                  whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
                                } else {
                                  whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
                                }
                                window.open(whatsappUrl, '_blank');
                              }}
                              title="Send password via WhatsApp"
                            >
                              <WhatsAppIcon />
                            </button>
                            )}
                          </div>
                        </td>
                        <td className="p-2 border">{user.partnerShip}</td>
                        <td className="p-2 border text-center">{user.matchCommission}</td>
                        <td className="p-2 border text-center">{user.sessionCommission}</td>
                        <td className="p-2 border text-center">{user.casinoCommission || 0}</td>
                        <td className="p-2 border text-center">{user.matkaCommission || 0}</td>
                        <td className="p-2 border">
                          {userType === USER_TYPES.CLIENT 
                            ? (
                                (user.balance || 0) + (user.balanceWithPnl || 0) - (user.liability || 0)
                              ).toFixed(2)
                            : (
                                (user.balance || 0) + (user.balanceWithPnl || 0)
                              ).toFixed(2)
                          }
                        </td>
                        {userType === USER_TYPES.CLIENT && (
                          <td className="p-2 border">
                            <span
                              className="cursor-pointer text-blue-600 hover:text-blue-800 font-semibold"
                              onClick={function () {
                                setClientExposureModal({
                                  isOpen: true,
                                  user: user,
                                });
                              }}
                            >
                              {(user.liability || 0).toFixed(2)}
                            </span>
                          </td>
                        )}
                        <td className="p-2 border">
                          <span
                            className={
                              user.isActive
                                ? "text-green-600 font-semibold"
                                : "text-red-600 font-semibold"
                            }
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination - Hide when search is active or custom data */}
        {!isSearchActive && !customData && !isDeadClient && usersByType && (
          <div className="flex justify-between items-center p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                className="px-2 py-1 border rounded text-sm"
                value={itemsPerPage}
                onChange={function (e) {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentRTKPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">
                Total: {usersByType.pagination?.totalUsers || usersByType.count || 0}
              </span>
            </div>
            <div className="flex gap-1">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={currentRTKPage === 1}
                onClick={function () {
                  setCurrentRTKPage(currentRTKPage - 1);
                }}
              >
                &lt;
              </button>
              <button className="px-3 py-1 bg-blue-500 text-white rounded">
                {currentRTKPage} / {usersByType.pagination?.totalPages || Math.ceil((usersByType.count || 0) / itemsPerPage) || 1}
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={currentRTKPage >= (usersByType.pagination?.totalPages || Math.ceil((usersByType.count || 0) / itemsPerPage) || 1)}
                onClick={function () {
                  setCurrentRTKPage(currentRTKPage + 1);
                }}
              >
                &gt;
              </button>
            </div>
          </div>
        )}
        
        {/* Old Pagination - Show only when using Redux state */}
        {!isSearchActive && !usersByType && !customData && !isDeadClient && (
          <div className="flex justify-end p-3 gap-1">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === 0}
              onClick={function () {
                fetchUsers(userType, "", currentPage - 1, 250);
              }}
            >
              &lt;
            </button>
            <button className="px-3 py-1 bg-blue-500 text-white rounded">
              {currentPage + 1}
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage >= totalPages - 1}
              onClick={function () {
                fetchUsers(userType, "", currentPage + 1, 250);
              }}
            >
              &gt;
            </button>
          </div>
        )}
      </div>

      {/* Search Dropdown */}
      <SearchDropdown
        isOpen={searchDropdown.isOpen}
        onClose={function () {
          setSearchDropdown({
            isOpen: false,
            field: null,
            position: { top: 0, left: 0 },
          });
        }}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loading={searchLoading}
        position={searchDropdown.position}
      />

      {/* Dropdown Menu */}
      {activeDropdown && (
        <UserActionsDropdown
          user={filteredUsers.find(function (u) {
            return u.userId === activeDropdown;
          })}
          onAction={handleAction}
          onClose={handleCloseDropdown}
          position={dropdownPosition}
          menuClassName="absolute bg-white rounded shadow-lg z-50 py-1 min-w-[180px] list-none m-0 p-0 max-h-[70vh] overflow-y-auto"
          menuStyle={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            boxShadow:
              "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
          }}
        />
      )}

      {/* Partnership Modal */}
      <PartnershipModal
        isOpen={partnershipModal.isOpen}
        onClose={handleClosePartnership}
        userId={formatUserId(partnershipModal.userId)}
        userMongoId={partnershipModal.mongoId}
      />

      {/* Transaction Modal (Deposit/Withdraw) */}
      <TransactionModal
        isOpen={transactionModal.isOpen}
        onClose={function () {
          setTransactionModal({ isOpen: false, type: null, user: null });
        }}
        type={transactionModal.type}
        user={transactionModal.user}
        onSuccess={handleTransactionSuccess}
      />

      {/* Password Modal */}
      <PasswordModal
        isOpen={passwordModal.isOpen}
        onClose={function () {
          setPasswordModal({ isOpen: false, user: null });
        }}
        user={passwordModal.user}
      />

      {/* User Exposure Modal */}
      <UserExposureModal
        isOpen={exposureModal.isOpen}
        onClose={function () {
          setExposureModal({ isOpen: false, user: null });
        }}
        userId={exposureModal.user?.userId}
        userName={exposureModal.user?.userName}
      />

      {/* Client Exposure Modal */}
      <ClientExposureModal
        isOpen={clientExposureModal.isOpen}
        onClose={function () {
          setClientExposureModal({ isOpen: false, user: null });
        }}
        userId={clientExposureModal.user?.id}
        userName={clientExposureModal.user?.userName}
      />

      {/* Block Action Modal */}
      <BlockActionModal
        isOpen={blockActionModal.isOpen}
        onClose={function () {
          setBlockActionModal({ isOpen: false, user: null });
        }}
        user={blockActionModal.user}
        onBlockAction={handleBlockAction}
      />
    </div>
  );
};

export default UserListPage;
